import { Query, JsonApiRequestError, Mutation } from '@farfetched/core';
import { chain } from '@leprecoins/effector';
import { createEvent, createEffect, createStore, EventCallable, sample, Store } from 'effector';
import { not, or } from 'patronum';
import { accountTreeModel } from 'entities/account';
import {
	USER_NAME_MAX_LENGTH,
	USER_NAME_MIN_LENGTH,
	USER_NOTE_MAX_LENGTH,
	USER_PASSWORD_MIN_LENGTH,
} from 'entities/user';
import {
	AddUserParams,
	AddUserResponse,
	createAddUserMutation,
	createGetAccountsQuery,
	EntityWithName,
	UserRole,
} from 'shared/api';
import {
	copyToClipboard,
	createModalFormModel,
	generatePassword,
	maxLengthRule,
	minLengthRule,
	ModalFormModel,
	notEmptyRule,
	notify,
} from 'shared/lib';

export type CreatedUserCredentials = {
	name: string;
	password: string;
};

export type AddUserFormValue = {
	name: string;
	password: string;
	accountId: number | null;
	role: UserRole | null;
	note: string;
	hasMultiLogin: boolean;
};

export type AddUserModel = ModalFormModel<null, AddUserFormValue> & {
	addUserMutation: Mutation<AddUserParams, AddUserResponse, JsonApiRequestError>;
	getAccountsQuery: Query<void, EntityWithName[], JsonApiRequestError>;
	$accounts: Store<EntityWithName[]>;
	$pending: Store<boolean>;
	$createdCredentials: Store<CreatedUserCredentials | null>;
	passwordGenerateClicked: EventCallable<void>;
	formPasswordCopyClicked: EventCallable<void>;
	usernameCopyClicked: EventCallable<void>;
	passwordCopyClicked: EventCallable<void>;
};

export const createAddUserModel = (): AddUserModel => {
	const addUserMutation = createAddUserMutation();

	const getAccountsQuery = createGetAccountsQuery();

	const model = createModalFormModel<null, AddUserFormValue>({
		fields: {
			name: {
				init: '',
				rules: [
					notEmptyRule,
					minLengthRule(USER_NAME_MIN_LENGTH),
					maxLengthRule(USER_NAME_MAX_LENGTH),
				],
			},
			password: {
				init: '',
				rules: [notEmptyRule, minLengthRule(USER_PASSWORD_MIN_LENGTH)],
			},
			accountId: {
				init: null,
				rules: [notEmptyRule],
			},
			role: {
				init: null,
				rules: [notEmptyRule],
			},
			note: {
				init: '',
				rules: [maxLengthRule(USER_NOTE_MAX_LENGTH)],
			},
			hasMultiLogin: {
				init: false,
			},
		},
		validateOn: ['submit'],
		resetErrorsOnChange: true,
	});

	const $accounts = getAccountsQuery.$data.map((result) => result ?? []);

	const $pending = or(getAccountsQuery.$pending, addUserMutation.$pending);

	const passwordGenerateClicked = createEvent();

	const formPasswordCopyClicked = createEvent();

	const usernameCopyClicked = createEvent();

	const passwordCopyClicked = createEvent();

	const $createdCredentials = createStore<CreatedUserCredentials | null>(null);

	const copyUsernameFx = createEffect((name: string) => copyToClipboard(name));

	const copyPasswordFx = createEffect((password: string) => copyToClipboard(password));

	const copyCredentialsFx = createEffect(
		(credentials: CreatedUserCredentials | null) =>
			credentials &&
			copyToClipboard(`Username: ${credentials.name}\nPassword: ${credentials.password}`),
	);

	sample({
		clock: [model.modalButtonClicked, passwordGenerateClicked],
		fn: () => generatePassword(12, USER_PASSWORD_MIN_LENGTH),
		target: model.form.fields.password.setValue,
	});

	sample({
		clock: addUserMutation.finished.success,
		fn: ({ params }) => ({ name: params.name, password: params.password }),
		target: $createdCredentials,
	});

	sample({
		clock: usernameCopyClicked,
		source: $createdCredentials,
		filter: Boolean,
		fn: (credentials) => credentials.name,
		target: copyUsernameFx,
	});

	sample({
		clock: passwordCopyClicked,
		source: $createdCredentials,
		filter: Boolean,
		fn: (credentials) => credentials.password,
		target: copyPasswordFx,
	});

	sample({
		clock: formPasswordCopyClicked,
		source: model.form.fields.password.$value,
		filter: (password) => password.length > 0,
		target: copyPasswordFx,
	});

	notify(copyUsernameFx.done, 'add-user.username-copied');

	notify(copyPasswordFx.done, 'add-user.password-copied');

	notify(copyCredentialsFx.done, 'add-user.credentials-copied');

	sample({
		clock: model.modalButtonClicked,
		target: [model.form.fields.accountId.disable, getAccountsQuery.start],
	});

	sample({
		clock: model.modalButtonClicked,
		source: accountTreeModel.$singleSelectedNode,
		filter: (accountId): accountId is number => accountId !== null,
		target: model.form.fields.accountId.setValue,
	});

	chain(getAccountsQuery.finished.success, model.form.fields.accountId.enable);

	sample({
		clock: model.form.submitted,
		source: model.form.$values,
		filter: not(addUserMutation.$succeeded),
		fn: (values) => ({
			name: values.name,
			password: values.password,
			accountId: values.accountId!,
			role: values.role!,
			note: values.note ?? undefined,
			hasMultiLogin: values.hasMultiLogin,
		}),
		target: addUserMutation.start,
	});

	sample({
		clock: model.form.submitted,
		source: $createdCredentials,
		filter: addUserMutation.$succeeded,
		target: copyCredentialsFx,
	});

	notify(addUserMutation.finished.success, 'add-user.success');

	sample({
		clock: model.modalClosed,
		fn: () => null,
		target: $createdCredentials,
	});

	sample({
		clock: model.modalClosed,
		target: [getAccountsQuery.reset, addUserMutation.reset],
	});

	return {
		getAccountsQuery,
		addUserMutation,
		$accounts,
		$pending,
		$createdCredentials,
		passwordGenerateClicked,
		formPasswordCopyClicked,
		usernameCopyClicked,
		passwordCopyClicked,
		...model,
	};
};
